import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from '../../widgets/Table'
import Icons from '../../assets/icons'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import { fetchCustomerUsers, fetchFFLs } from './customers.asynActions'
import {
	selectAccounts,
	selectFilters,
	selectSelectedAccounts,
	selectUsers,
} from './customers.selectors'
import { actions } from './customers.slice'
import {
	ACCOUNT_TYPE,
	CUSTOMER_MODALS,
	CUSTOMER_USER_ORIGIN_ICONS,
	CUSTOMERS_USER_ROLES,
} from './customers.constants'
import SortingCustomIcon from '../../widgets/SortingCustomIcon'
import { getLocalDateFromUTC } from '../../widgets/CustomDateRangePicker/CustomDateRangePicker.utils'
import { selectIsAdmin } from '../Profile/profile.selectors'
import { REGIONS, getNamedBusyIndicator } from '../../widgets/busyIndicator'
import { DataTable } from '@/components/DataTable'
import { getJwtToken } from '../../features/userContext/userContext.selectors'
import {
	getFormattedAccountName,
	getFormattedPlanName,
	getFormattedTitle,
} from '../Reports/reports.constants'
import { getDelinquencyPills } from './customerDelinquency'
import { Badge } from '@/components/ui/badge'
import { ResolveUserOriginWithUserId } from '../../helpers/IntegrationPartnerHelpers'
import { isEmpty } from 'lodash'
import { actions as notificationActions } from '../../infrastructure/notificationPopup/notificationPopup.slice'
import { Link } from 'react-router-dom'
import UsersTable from '@/features/users/components/UsersTable'
import { useEffect } from 'react'

const {
	setSelectedCustomerModal,
	setSelectedAccounts,
	resetUsers,
	setSelectedRow,
	setSelectedAccount,
	setCustomerFilters,
} = actions

const ACTIVATED = 'Activated'
const INVITED = 'Invited'

const CustomersTable = () => {
	const dispatch = useDispatch()
	const users = useSelector(selectUsers)
	const accounts = useSelector(selectAccounts)
	const filters = useSelector(selectFilters)
	const isLoadingAccounts = useSelector(getNamedBusyIndicator(REGIONS.FETCH_CUSTOMER_ACCOUNTS))

	const { data, page, totalItems } = accounts
	const { limit } = filters

	const selectedAccounts = useSelector(selectSelectedAccounts)
	const isAdmin = useSelector(selectIsAdmin)

	const jwtToken = useSelector(getJwtToken)

	const isEvenIndex = (n) => n % 2 === 0

	const varianceDataFormatter = () => null

	const assignmentFormatter = (id, row, idx) => (
		<div className='ml-18'>
			<span className='text-nowrap d-block'>
				{row.assignments[0] && row.assignments[0].name}
			</span>
			{row.assignments.length > 1 && (
				<span className='f-10 font-semi-bold d-block'>
					+ {row.assignments.length - 1} more
				</span>
			)}
		</div>
	)

	const userOptions = (id, row, idx) => {
		return (
			<div className='d-flex justify-content-end'>
				<div
					className='pointer f-5 font-semi-bold'
					onClick={() =>
						dispatch(
							setSelectedCustomerModal(
								CUSTOMER_MODALS.RESET_CUSTOMER_USER_PASSWORD
							)
						)
					}
				>
                    <img width='15' src={Icons.Reset} className='mb-1 mr-2 inline-block' />
					Reset Password
				</div>
				<div
					className='f-5 pointer font-semi-bold d-flex ml-20'
					onClick={() =>
						dispatch(
							setSelectedCustomerModal(CUSTOMER_MODALS.DELETE_USER_MODAL)
						)
					}
				>
                    <img src={Icons.Delete} width='15' alt='delete-icon' className='mb-1 mr-2 inline-block' />
					Delete User
				</div>
				<div
					className='f-5 pointer font-semi-bold d-flex ml-20'
				onClick={() => {
					const url = import.meta.env.VITE_E4473_URL
					if (row.id && jwtToken && url) {
						try {
							window.open(`${url}?token=${jwtToken}&id=${row.id}`, '_blank')
						} catch {
							dispatch(notificationActions.notifyError('Something went wrong. Unable to impersonate.'))
						}
					} else {
						dispatch(notificationActions.notifyError('Something went wrong. Unable to impersonate.'))
					}
				}}
				>
                    <img width='15' className='mb-1 mr-2 inline-block' src={isEvenIndex(idx) ? Icons.User : Icons.UserIcon} />
					Impersonate
				</div>
			</div>
		)
	}

	const activateDateFormatter = (id, row) =>
		row && row.activationDate && getLocalDateFromUTC(row.activationDate)

	const handleOnSelect = (row, isSelect) => {
		if (isSelect) {
			dispatch(setSelectedAccounts([...selectedAccounts, row]))
		} else {
			dispatch(
				setSelectedAccounts(selectedAccounts.filter((x) => x.id !== row.id))
			)
		}
	}

	const roleFormatter = (id, row) => CUSTOMERS_USER_ROLES[row.role]

	function fflNumberFormatter(data) {
		return !isEmpty(data) && data.length ? (
			<div>
				<span className='text-nowrap d-block'>{data[0]}</span>
				{data.length > 1 && (
					<span className='f-10 font-semi-bold d-block'>
						+ {data.length - 1} more
					</span>
				)}
			</div>
		) : null
	}

	const getActivationStatus = (id, row) =>
		row.activationDate ? ACTIVATED : INVITED

	const getAccountType = (id, row) => ACCOUNT_TYPE[row.type]

	const getAccountNameWithDelinquentPill = (id, row) => {
		const name = getFormattedAccountName(id, row)
		const pills = getDelinquencyPills(row)
		if (!pills.length) return name
		return (
			<div className='flex flex-wrap items-center gap-2'>
				<span>{name}</span>
				<div className='flex items-center gap-2'>
					{pills.map((pill) => (
						<Badge
							key={pill.key}
							variant={pill.key === 'balance' ? 'warning' : 'destructive'}
							className='shrink-0 whitespace-nowrap'
						>
							{pill.label}
						</Badge>
					))}
				</div>
			</div>
		)
	}

	const actionsFormatter = (id, row) => (
		isAdmin ? (
			<div className='d-flex justify-content-end'>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' className='h-8 w-8 p-0'>
							<span className='sr-only'>Open menu</span>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
					<DropdownMenuItem asChild>
							<Link to={`/accounts/${row.id}`}>View Account</Link>
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => {
							dispatch(setSelectedAccount(row))
							dispatch(fetchFFLs({ id: row.id, useCaching: false }))
							dispatch(setSelectedCustomerModal(CUSTOMER_MODALS.VARIANCE_DATA_MODAL))
						}}>View FFL data</DropdownMenuItem>
						<DropdownMenuItem onClick={() => {
							dispatch(setSelectedAccount(row))
							dispatch(setSelectedCustomerModal(CUSTOMER_MODALS.EDIT_ASSIGNMENT_MODAL))
						}}>Edit assignment</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		) : null
	)

	const CheckboxRenderer = ({ checked, mode, ...rest }) =>
		isAdmin && (
			<div className='custom-checkbox d-block'>
				<label>
					<input type={mode} checked={checked} {...rest} onChange={() => {}} />
					<span></span>
				</label>
			</div>
		)

	const selectRow = {
		mode: 'checkbox',
		onSelect: handleOnSelect,
		clickToSelect: false,
		hideSelectAll: true,
		selectionHeaderRenderer: CheckboxRenderer,
		selectionRenderer: CheckboxRenderer,
		selected: selectedAccounts.map((each) => each.id),
	}

	const ACCOUNTS_COLUMNS = [
		{
			dataField: 'account',
			title: getFormattedTitle,
			text: 'Account',
			headerStyle: { width: '280px' },
			align: 'left',
			sort: true,
			formatter: getAccountNameWithDelinquentPill,
			sortCaret: SortingCustomIcon,
			footer: ({ table }) => {
			
				return `Total: ${table.getRowModel().rows.length}`
			},
		},
		{
			dataField: 'plan',
			text: 'Plan',
			headerStyle: { width: '250px' },
			sort: true,
			formatter: getFormattedPlanName,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: '',
			text: 'Activation',
			headerStyle: { width: '240px' },
			sort: true,
			sortCaret: SortingCustomIcon,
			formatter: getActivationStatus,
		},
		{
			dataField: 'activationDate',
			text: 'Activation Date',
			formatter: activateDateFormatter,
			headerStyle: { width: '350px' },
			sort: true,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'renewalDate',
			text: 'Renewal Date',
			formatter: getLocalDateFromUTC,
			headerStyle: { width: '220px' },
			sort: true,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'type',
			text: 'Account Type',
			headerStyle: { width: '250px' },
			sort: true,
			formatter: getAccountType,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'fflNumbers',
			text: 'FFL Numbers',
			formatter: fflNumberFormatter,
		},
		{
			dataField: 'backupToolInstalled',
			text: 'Backup Tool Installed',
			sort: true,
			classes: 'text-center',
			formatter: (data) => (data ? 'Yes' : 'No'),
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'backupToolDownloadClicked',
			text: 'Backup Tool Download Clicked',
			sort: true,
			classes: 'text-center',
			formatter: (data) => (data ? 'Yes' : 'No'),
			sortCaret: SortingCustomIcon,
		},
				{
			dataField: 'actions',
			text: '',
			formatter: actionsFormatter,
			headerStyle: { width: '160px' },
			sort: false,
		},
	]

	function UserNameFormatter(name, customer) {
		return (
			<>
				{CUSTOMER_USER_ORIGIN_ICONS[ResolveUserOriginWithUserId(customer.id)]}
				{name}
			</>
		)
	}

	const USERS_COLUMNS = [
		{
			dataField: 'userName',
			text: 'User Name',
			sort: true,
			sortCaret: SortingCustomIcon,
			headerStyle: { width: '160px' },
			formatter: UserNameFormatter,
		},
		{
			dataField: 'role',
			text: 'Role',
			sort: true,
			formatter: roleFormatter,
			sortCaret: SortingCustomIcon,
			headerStyle: { width: '130px' },
		},
		{
			dataField: 'email',
			text: 'Email',
			sort: true,
			sortCaret: SortingCustomIcon,
			headerStyle: { width: '160px' },
		},
		{
			dataField: 'lastLogin',
			text: 'Last Login',
			sort: true,
			sortCaret: SortingCustomIcon,
			formatter: getLocalDateFromUTC,
			headerStyle: { width: '160px' },
		},
		{
			dataField: '',
			text: '',
			formatter: userOptions,
		},
	]

	const rowEvents = {
		onClick: (e, row) => {
			dispatch(setSelectedRow(row))
		},
	}

	const expandRow = {
		// eslint-disable-next-line
		renderer: (row, rowIndex) => (
			<div key={rowIndex}>
				<Table
					keyField='id'
					data={users}
					columns={USERS_COLUMNS}
					innerTable={true}
					rowEvents={rowEvents}
					region={REGIONS.FETCH_CUSTOMER_USERS}
					isDisplayTable={true}
				/>
			</div>
		),
		onlyOneExpanding: true,
		showExpandColumn: true || false,
		expandColumnPosition: 'right',
		expandByColumnOnly: false,
		// eslint-disable-next-line
		expandHeaderColumnRenderer: () => <div></div>,
		onExpand: (row, isExpand) => {
			isExpand &&
				dispatch(fetchCustomerUsers({ id: row.id, useCaching: false }))
		},
		// eslint-disable-next-line
		expandColumnRenderer: ({ expanded, rowKey }) => (
			<div className='btn-auto p-0 d-inline-flex align-items-center f-5 pointer font-semi-bold align-left text-nowrap'>
				{expanded &&
				dispatch(resetUsers()) &&
				dispatch(setSelectedAccount(rowKey))
					? 'View Less'
					: 'View More'}
                {<img src={expanded ? Icons.ArrowUp : Icons.ArrowDown} width='15' className='ml-2 inline-block' alt='arrow-icon' />}
			</div>
		),
	}

    const handleServerPaginationChange = (updater) => {
        const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize: limit }) : updater
        const nextPageIndex = next.pageIndex ?? 0
        const nextPageSize = next.pageSize ?? limit
        dispatch(
            setCustomerFilters({
                page: nextPageIndex + 1,
                limit: nextPageSize,
            })
        )
    }

	// Remote sorting derived from filters
	const sorting = React.useMemo(() => {
		if (filters.sort) {
			return [{ id: filters.sort, desc: (filters.sortOrder || '').toLowerCase() === 'desc' }]
		}
		return []
	}, [filters.sort, filters.sortOrder])

	const handleServerSortingChange = (updater) => {
		const next = typeof updater === 'function' ? updater(sorting) : updater
		const first = Array.isArray(next) && next.length ? next[0] : null
		dispatch(
			setCustomerFilters({
				sort: first ? first.id : '',
				sortOrder: first ? (first.desc ? 'desc' : 'asc') : '',
				page: 1,
			})
		)
	}

	const mappedColumns = React.useMemo(() => {
        if (!ACCOUNTS_COLUMNS || Array.isArray(ACCOUNTS_COLUMNS) === false) return []
        return ACCOUNTS_COLUMNS.map((col, idx) => {
            const accessorKey = col.dataField || undefined
			const safeId = accessorKey || (typeof col.text === 'string' ? col.text : `col_${idx}`)
            return {
				id: accessorKey ? String(accessorKey) : (String(safeId) || `col_${idx}`),
                header: col.text,
                accessorKey,
                cell: col.formatter
                    ? ({ getValue, row }) => col.formatter(getValue?.(), row.original)
                    : ({ getValue }) => getValue?.(),
				enableSorting: !!(accessorKey && col.sort),
            }
        })
    }, [ACCOUNTS_COLUMNS])

	const ExpandedAssignments = ({ row }) => {
		useEffect(() => {
			
		}, [row.original])

		return (
	
				<UsersTable accountId={row.original?.id} />
		)
	}

    return (
        
			<DataTable
				columns={mappedColumns}
				data={data || []}
				isLoading={isLoadingAccounts}
				loadingMessage={'Loading customers...'}
				emptyMessage={'No Data Found'}
				enablePagination={true}
				serverPagination={true}
				scrollContainerClassName='max-h-[calc(100vh-500px)] overflow-y-auto'
				pageIndex={(page || 1) - 1}
				pageSize={limit}
				pageCount={Math.max(1, Math.ceil((totalItems || 0) / (limit || 1)))}
				onPaginationChange={handleServerPaginationChange}
				serverSorting={true}
				sorting={sorting}
				onSortingChange={handleServerSortingChange}
				expandableContent={(row) => {
					return <ExpandedAssignments row={row} />
				}}
			/>
    )
}

export default CustomersTable
